import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { activitiesAPI, tasksAPI, projectsAPI, usersAPI, timeTrackingAPI } from '../utils/api';
import { PageHeader, Badge, Modal, FormField, SelectField, EmptyState, ConfirmDialog } from '../components/layout/UI';
import { format, parseISO, isValid } from 'date-fns';
import { useAuth } from '../hooks/useAuth';
import DateWarnings, { useDateWarnings } from '../components/DateWarnings';
import { useToast, apiError, useConfirm } from '../components/Toast';
import { useMaster } from '../hooks/useMasters';
import { usePagePerms } from '../PagePermsContext';
import { Calendar, CheckCircle, Circle, Clock, Download, Layers, Plus, RotateCcw, SquarePen, Trash2 } from 'lucide-react';
import { getPermLevel, isAdminRole } from '../utils/rolesConfig';
import Pager, { usePaging } from '../components/Pager';
import { useSelection, SelectAll, RowCheck, BulkBar } from '../components/BulkActions';
import { STATUS_OPTIONS, WORK_STATUSES, statusStyle, isSettled } from '../utils/workStatus';

// ── Icons ─────────────────────────────────────────────────────────────────
const PlusIcon = () => <Plus size={14} />;
const PencilIcon = () => <SquarePen size={13} />;
const TrashIcon = () => <Trash2 size={13} />;
const CircleIcon = () => <Circle size={16} />;
const InProgressIcon = () => <Clock size={16} />;
const DlIcon = () => <Download size={14} />;
const CalIcon = () => <Calendar size={12} />;
const ClockLogIcon = () => <Clock size={13} />;

const STATUS_OPTS_FALLBACK = STATUS_OPTIONS;
// Matches activities_priority_check (phase43) and tasks.priority.
const PRIORITY_OPTS_FALLBACK = ['Low','Medium','High','Critical'].map(v => ({ value: v, label: v }));

const safeFormat = (val, fmt) => {
  if (!val) return null;
  try {
    const d = typeof val === 'string' ? parseISO(val) : new Date(val);
    return isValid(d) ? format(d, fmt) : null;
  } catch { return null; }
};

// A coloured dot for the two states that had no icon of their own and so
// rendered exactly like Pending — a paused or missed activity looked
// untouched in a list of twenty.
const StatusDot = ({ status }) => (
  <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
    title={status} style={{ background: statusStyle(status).dot }} />
);

const StatusIcon = ({ status }) => {
  if (status === 'Completed')   return <CheckCircle size={16} color="#10b981"/>;
  if (status === 'In Progress') return <InProgressIcon />;
  if (status === 'Hold' || status === 'Skipped') return <StatusDot status={status} />;
  return <CircleIcon />;
};

const EffortBar = ({ estimated, actual }) => {
  const est = parseFloat(estimated) || 0;
  const act = parseFloat(actual) || 0;
  if (!est) return <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>—</span>;
  const pct = Math.min(100, Math.round((act / est) * 100));
  const over = act > est;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'var(--border)', minWidth: 60 }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: over ? '#ef4444' : pct > 80 ? '#f59e0b' : '#2E539F' }} />
      </div>
      <span className="text-xs whitespace-nowrap font-medium" style={{ color: over ? '#ef4444' : 'var(--text-secondary)' }}>
        {act.toFixed(1)}/{est.toFixed(1)}h
      </span>
    </div>
  );
};

const defaultForm = {
  task_id: '', title: '', description: '',
  estimated_hours: '',
  status: 'Pending', priority: 'Medium', assigned_to: '',
  start_date: '', due_date: '',
  recurrence: 'none', recurrence_until: '',
};

// Matches activities_recurrence_check (phase65).
const RECURRENCE = [
  { v: 'none',     l: 'Does not repeat' },
  { v: 'daily',    l: 'Every day' },
  { v: 'weekdays', l: 'Every weekday (Mon–Fri)' },
  { v: 'weekly',   l: 'Every week, on the same weekday' },
];
const WEEKDAY_OF = (d) => d
  ? new Date(String(d).slice(0,10) + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long' })
  : '';

// Converts an ISO timestamp into the format <input type="datetime-local"> needs
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = n => String(n).padStart(2,'0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ── Log Time modal: shows past logged sessions first (editable/deletable),
// plus a form to add a new one. Opening it on an activity that already has
// logged time shows that history, not a blank "start from scratch" form.
function LogTimeModal({ isOpen, onClose, activity, onLogged }) {
  // Its own component, so it needs its own hook — the one on the page below
  // is out of scope here.
  const toast = useToast();
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // null = adding new, id = editing that log
  const [startAt, setStartAt] = useState('');
  const [endAt,   setEndAt]   = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const loadLogs = useCallback(async () => {
    if (!activity) return;
    setLoading(true);
    try { const r = await timeTrackingAPI.getLogs(activity.id); setLogs(r.data); }
    catch(e) { toast.error(apiError(e, 'Something went wrong loading this.')); }
    finally { setLoading(false); }
  }, [activity]);

  useEffect(() => {
    if (isOpen) { setEditingId(null); setStartAt(''); setEndAt(''); setError(''); loadLogs(); }
  }, [isOpen, loadLogs]);

  const startEdit = (log) => {
    setEditingId(log.id);
    setStartAt(toLocalInput(log.started_at));
    setEndAt(toLocalInput(log.ended_at));
    setError('');
  };

  const startNew = () => {
    setEditingId(null);
    setStartAt(''); setEndAt(''); setError('');
  };

  const durationLabel = (() => {
    if (!startAt || !endAt) return null;
    const diffMs = new Date(endAt) - new Date(startAt);
    if (diffMs <= 0) return null;
    const totalMin = Math.round(diffMs / 60000);
    const h = Math.floor(totalMin / 60), m = totalMin % 60;
    return `${h}h ${m}m`;
  })();

  const handleSave = async () => {
    if (!startAt || !endAt) return;
    if (new Date(endAt) <= new Date(startAt)) { setError('End time must be after start time'); return; }
    setSaving(true); setError('');
    try {
      const payload = { started_at: new Date(startAt).toISOString(), ended_at: new Date(endAt).toISOString() };
      if (editingId) {
        await timeTrackingAPI.updateLog(editingId, payload);
      } else {
        await timeTrackingAPI.logManual(activity.id, payload);
      }
      await loadLogs();
      onLogged();
      startNew();
    } catch(e) { setError(e.response?.data?.message || 'Could not save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (logId) => {
    if (!await confirm('Delete this logged session?')) return;
    try {
      await timeTrackingAPI.deleteLog(logId);
      await loadLogs();
      onLogged();
      if (editingId === logId) startNew();
    } catch(e) { toast.error(apiError(e, 'Failed to save activity. Please try again.')); }
  };

  if (!activity) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Log Time - ${activity.title}`} size="md">
      <div className="space-y-4">

        {/* Existing sessions */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color:'var(--text-secondary)' }}>Logged Sessions</p>
          {loading ? (
            <p className="text-xs" style={{ color:'var(--text-secondary)' }}>Loading...</p>
          ) : logs.length === 0 ? (
            <p className="text-xs" style={{ color:'var(--text-secondary)' }}>No time logged yet on this activity</p>
          ) : (
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {logs.map(log => {
                const h = Math.floor(log.duration_minutes/60), m = log.duration_minutes%60;
                return (
                  <div key={log.id}
                    className={`flex items-center justify-between gap-2 p-2 rounded-lg border ${editingId===log.id ? 'border-brand-500' : ''}`}
                    style={{ borderColor: editingId===log.id ? undefined : 'var(--border)', background:'var(--surface-alt)' }}>
                    <div className="text-xs">
                      <span className="font-medium" style={{ color:'var(--text)' }}>{h}h {m}m</span>
                      <span className="ml-2" style={{ color:'var(--text-secondary)' }}>
                        {new Date(log.started_at).toLocaleString([], { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}
                        {' → '}
                        {new Date(log.ended_at).toLocaleString([], { hour:'2-digit', minute:'2-digit' })}
                      </span>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={()=>startEdit(log)} className="text-xs px-2 py-1 rounded text-brand-500 hover:bg-brand-500/10">Edit</button>
                      <button onClick={()=>handleDelete(log.id)} className="text-xs px-2 py-1 rounded text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">Delete</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Add / Edit form */}
        <div className="rounded-xl border p-3 space-y-3" style={{ borderColor:'var(--border)' }}>
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>
              {editingId ? 'Edit Session' : 'Log New Session'}
            </p>
            {editingId && <button onClick={startNew} className="text-xs text-brand-500 hover:underline">+ Log new instead</button>}
          </div>
          <FormField label="Start" required>
            <input type="datetime-local" className="input" value={startAt} onChange={e=>setStartAt(e.target.value)} />
          </FormField>
          <FormField label="End" required>
            <input type="datetime-local" className="input" value={endAt} onChange={e=>setEndAt(e.target.value)} />
          </FormField>
          {durationLabel && (
            <div className="rounded-lg px-3 py-2 text-sm font-medium" style={{ background:'var(--surface-alt)', color:'#2E539F' }}>
              Duration: {durationLabel}
            </div>
          )}
          {error && <p className="text-xs text-red-500">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="btn-secondary">Close</button>
            <button onClick={handleSave} disabled={saving || !startAt || !endAt} className="btn-primary flex items-center gap-2">
              {saving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
              {editingId ? 'Update Session' : 'Log Time'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default function ActivitiesPage() {
  // Admin → Page Access decides whether this role may create here.
  const { canWrite } = usePagePerms();
  // Dropdowns come from Masters so an Admin can relabel and reorder them.
  // The _FALLBACK arrays above are used when Masters has nothing configured.
  const STATUS_OPTS = useMaster('activity_status', STATUS_OPTS_FALLBACK);
  const PRIORITY_OPTS = useMaster('priority', PRIORITY_OPTS_FALLBACK);
  const confirm = useConfirm();
  const toast = useToast();
  const { user: me } = useAuth();
  /**
   * Mirrors the API exactly: the creator, the assignee, or an admin. A
   * button that is always refused is worse than no button — the person
   * cannot tell a permission from a fault.
   */
  /**
   * Seeing and changing are different things. Everyone keeps the view;
   * only the creator, the assignee or an admin may alter the work.
   */
  const canEdit = (a) =>
    isAdminRole(me?.role) || getPermLevel(me?.role) === 'Manager' || a.created_by === me?.id || a.assigned_to === me?.id;

  const canRemove = (a) =>
    isAdminRole(me?.role) || getPermLevel(me?.role) === 'Manager' || a.created_by === me?.id || a.assigned_to === me?.id;
  const dateWarn = useDateWarnings();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const taskFilter    = searchParams.get('task_id')    || '';
  const projectFilter = searchParams.get('project_id') || '';

  const [activities,       setActivities]       = useState([]);
  const [tasks,            setTasks]            = useState([]);
  const [projects,         setProjects]         = useState([]);
  const [users,            setUsers]            = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [selectedTaskId,   setSelectedTaskId]   = useState(taskFilter);
  // All / Mine / Deleted, as one control.
  const [scope, setScope] = useState('all');
  // One pager for every list, so they all count and phrase it the same way.
  const paging = usePaging(scope);

  const [selectedProjectId,setSelectedProjectId]= useState(projectFilter);
  const [statusFilter,     setStatusFilter]     = useState('');
  const [taskSummary,      setTaskSummary]      = useState(null);
  const [taskInfo,         setTaskInfo]         = useState(null);

  const [modalOpen,    setModalOpen]    = useState(false);
  const [editActivity, setEditActivity] = useState(null);
  const [form,         setForm]         = useState({ ...defaultForm, task_id: taskFilter });
  const [saving,       setSaving]       = useState(false);
  const [deleteId,     setDeleteId]     = useState(null);
  const [logTimeFor,   setLogTimeFor]   = useState(null);

  const loadSupport = useCallback(async () => {
    try {
      const [pRes, uRes] = await Promise.all([projectsAPI.getAll({ limit: 0 }), usersAPI.getAll()]);
      setProjects(pRes.data);
      paging.readHeaders(pRes);
      setUsers(uRes.data);
      if (selectedProjectId) {
        const tRes = await tasksAPI.getAll({ project_id: selectedProjectId , limit: 0 });
        setTasks(tRes.data);
      } else {
        const tRes = await tasksAPI.getAll({ limit: 0 });
        setTasks(tRes.data);
      }
    } catch(e) { toast.error(apiError(e, 'Failed to save activity')); }
  }, [selectedProjectId]);

  const loadActivities = useCallback(async () => {
    setLoading(true);
    try {
      // task_id is optional now. 'All' means every activity you can see, not
      // 'pick a task first' — a deleted activity was unreachable, because you
      // cannot pick the task of something you can no longer see.
      const res = await activitiesAPI.getAll({
        task_id: selectedTaskId || undefined,
        project_id: selectedProjectId || undefined,
        scope:   scope === 'deleted' ? 'deleted' : undefined,
        mine:    scope === 'mine' ? 'true' : undefined, limit: paging.limit, offset: paging.offset });
      setActivities(res.data.activities || []);
      setTaskSummary(res.data.summary || null);
      setTaskInfo(res.data.task || null);
    } catch(e) { toast.error(apiError(e, 'Could not load activities.')); }
    finally { setLoading(false); }
  }, [selectedTaskId, selectedProjectId, scope, toast, paging.offset]);


  const discardActivity = async (a) => {
    const ok = await confirm(
      'Remove this from your Deleted tab? You will not be able to see it after '
      + 'this, though an administrator can still restore it.',
      { title: 'Remove from Deleted', confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try {
      await activitiesAPI.discard(a.id);
      toast.success('Removed from your Deleted tab');
      loadActivities();
    } catch (e) { toast.error(apiError(e, 'Could not remove it.')); }
  };
  const restoreActivity = async (a) => {
    try {
      await activitiesAPI.restore(a.id);
      toast.success('Restored');
      loadActivities();
    } catch (e) { toast.error(apiError(e, 'Could not restore that activity.')); }
  };

  useEffect(() => { loadSupport(); },    [loadSupport]);
  useEffect(() => { loadActivities(); }, [loadActivities]);

  /** Browsers grey out-of-range days inconsistently and a value can still be
   *  typed, so clamping makes the bound real rather than a suggestion. */
  const clamp = (value, { min, max }) => {
    if (!value) return value;
    if (min && value < min) return min;
    if (max && value > max) return max;
    return value;
  };

  const rangeHint = () => {
    const b = taskBounds();
    return b.min || b.max
      ? `Must fall within the task: ${b.min || 'any'} to ${b.max || 'any'}`
      : 'This task has no dates set, so there is nothing to bound to';
  };

  /** These two dates are a DEADLINE WINDOW, not a schedule. When the work is
   *  actually done is set on the Workplan, which is why an activity due Friday
   *  does not automatically occupy Monday through Friday. */
  const DATE_EXPLAINER =
    'This is the window the work must fall in \u2014 not when it is scheduled. ' +
    'Use the Workplan to choose the day (or days) you will actually do it.';

  /** The parent task's window, as min/max for the pickers. The browser greys
   *  out everything outside it, so an activity cannot be scheduled before its
   *  task starts or after it ends — prevented rather than warned about. */
  const taskBounds = () => ({
    min: taskInfo?.start_date ? taskInfo.start_date.split('T')[0] : '',
    max: taskInfo?.due_date   ? taskInfo.due_date.split('T')[0]   : '',
  });

  const prefillDates = (task) => ({
    start_date: task?.start_date ? task.start_date.split('T')[0] : '',
    due_date:   task?.due_date   ? task.due_date.split('T')[0]   : '',
  });

  const openCreate = () => {
    setEditActivity(null);
    const dates = prefillDates(taskInfo);
    setForm({ ...defaultForm, task_id: selectedTaskId, assigned_to: me?.id || '', ...dates });
    setModalOpen(true);
  };

  const openEdit = (a) => {
    setEditActivity(a);
    setForm({
      task_id:         a.task_id,
      title:           a.title,
      description:     a.description || '',
      estimated_hours: a.estimated_hours || '',
      status:          a.status,
      priority:        a.priority || 'Medium',
      assigned_to:     a.assigned_to || '',
      start_date:      a.start_date ? a.start_date.split('T')[0] : (taskInfo?.start_date ? taskInfo.start_date.split('T')[0] : ''),
      due_date:        a.due_date   ? a.due_date.split('T')[0]   : (taskInfo?.due_date   ? taskInfo.due_date.split('T')[0]   : ''),
      recurrence:       a.recurrence || 'none',
      recurrence_until: a.recurrence_until ? String(a.recurrence_until).split('T')[0] : '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title) return;
    setSaving(true);
    try {
      // actual_hours is deliberately never part of this payload - it's a
      // computed rollup from logged time, never manually editable.
      const payload = {
        task_id:         editActivity?.task_id || selectedTaskId || form.task_id,
        title:           form.title,
        description:     form.description || null,
        estimated_hours: form.estimated_hours !== '' ? parseFloat(form.estimated_hours) : 0,
        status:          form.status,
        // Was missing. The modal has had a Priority field all along and never
        // sent it, so every activity was created at the default and an edit
        // silently kept the old value — which is also why nothing could ever
        // be marked important on the To-Do matrix.
        priority:        form.priority || 'Medium',
        assigned_to:     form.assigned_to || null,
        start_date:      form.start_date || null,
        due_date:        form.due_date   || null,
        recurrence:      form.recurrence || 'none',
        // Only meaningful while it repeats; cleared otherwise so an old end
        // date cannot come back when the repeat is turned on again.
        recurrence_until: form.recurrence !== 'none' ? (form.recurrence_until || null) : null,
      };
      if (editActivity) {
        const saved = await activitiesAPI.update(editActivity.id, payload);
        toast.success('Activity updated');
        dateWarn.show(saved.data);
      } else {
        const saved = await activitiesAPI.create(payload);
        toast.success('Activity created');
        dateWarn.show(saved.data);
      }
      setModalOpen(false);
      loadActivities();
    } catch(e) { toast.error(apiError(e, 'Failed to save activity.')); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await activitiesAPI.delete(deleteId);
    toast.success('Activity deleted'); setDeleteId(null); loadActivities(); } catch(e) { toast.error(apiError(e, 'Failed to delete activity.')); }
  };

  const handleInlineUpdate = async (a, patch) => {
    try { await activitiesAPI.update(a.id, { ...a, ...patch });
    toast.success('Activity updated'); loadActivities(); } catch(e) { toast.error(apiError(e, 'Failed to update status')); }
  };

  const handleStatusCycle = (a) => {
    const next = { Pending: 'In Progress', 'In Progress': 'Completed', Completed: 'Pending' }[a.status];
    // No longer auto-fills actual_hours on completion - that number must
    // only ever reflect real logged time, never an assumption.
    handleInlineUpdate(a, { status: next });
  };

  const filtered   = activities.filter(a => !statusFilter || a.status === statusFilter);

  // Ticked rows. Scoped to what is on screen, so a filter cannot hide
  // something that is still selected.
  // Declared AFTER `filtered`, deliberately: a const cannot be read
  // above its own declaration, and doing so crashes the page at render
  // with "Cannot access 'filtered' before initialization".
  const sel = useSelection(filtered);
  const selectedTask = tasks.find(t => t.id === selectedTaskId);
  const s = taskSummary;

  return (
    <div>
      <DateWarnings {...dateWarn} />
      <PageHeader
        title="Activities"
        subtitle="Track effort and progress at the activity level"
        actions={
          <div className="flex items-center gap-2">
            {selectedTaskId && (
              <button onClick={async () => {
                const { exportAPI, downloadBlob } = await import('../utils/api');
                const r = await exportAPI.exportActivities(tasks.find(t=>t.id===selectedTaskId)?.project_id);
                downloadBlob(r.data, 'activities.xlsx');
              }} className="btn-secondary flex items-center gap-1.5 text-sm">
                <DlIcon /> Export
              </button>
            )}
            {/* Ungated on purpose: POST /api/activities allows any authenticated
                user, and the empty-state button has never been gated either. */}
            <button
              onClick={openCreate}
              disabled={!selectedTaskId}
              title={!selectedTaskId ? 'Select a project and task first' : 'Add activity'}
              className="btn-primary flex items-center gap-1.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed">
              <PlusIcon /> Add Activity
            </button>
            
          </div>
        }
      />

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <div className="card mb-5">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-40">
            <label className="label">Project</label>
            <select className="select text-sm" value={selectedProjectId}
              onChange={e => { setSelectedProjectId(e.target.value); setSelectedTaskId(''); }}>
              <option value="">All Projects</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Show</label>
            {/* All / Deleted. "All" is every activity, which is why the task
                dropdown now says "All tasks" rather than asking you to pick one
                — a deleted activity has no task you can pick. */}
            <div className="flex rounded-lg border overflow-hidden text-sm"
              style={{ borderColor:'var(--border)' }}>
              {[['all','All'],['mine','Mine'],['deleted','Deleted']].map(([v,l]) => (
                <button key={v} onClick={() => setScope(v)}
                  className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1.5 ${scope===v ? 'bg-brand-500 text-white' : ''}`}
                  style={scope!==v ? { color:'var(--text-secondary)', background:'var(--surface)' } : {}}>
                  {v === 'deleted' && <Trash2 size={13} />}
                  {l}
                  {scope===v && <span className="font-bold">{activities.length}</span>}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-48">
            <label className="label">Task</label>
            <select className="select text-sm" value={selectedTaskId}
              onChange={e => setSelectedTaskId(e.target.value)}>
              <option value="">All tasks</option>
              {tasks
                .filter(t => !selectedProjectId || t.project_id === selectedProjectId)
                .map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-36">
            <label className="label">Status</label>
            <select className="select text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              {STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ── Task info banner ────────────────────────────────────────────────── */}
      {(selectedTask || taskInfo) && s && (() => {
        const t = taskInfo || selectedTask;
        const total = parseInt(s.total) || 0;
        const done  = parseInt(s.completed) || 0;
        const pct   = total > 0 ? Math.round((done / total) * 100) : 0;
        return (
          <div className="card mb-5">
            <div className="flex items-start justify-between gap-4 mb-4 pb-4 border-b" style={{ borderColor: 'var(--border)' }}>
              <div className="flex-1">
                <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-secondary)' }}>
                  {t?.project_name && `${t.project_name} · `}Task
                </p>
                <h2 className="font-bold text-base" style={{ color: 'var(--text)' }}>{t?.title}</h2>
                <div className="flex items-center gap-4 mt-2">
                  {t?.start_date && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      <CalIcon /> Start: <strong style={{ color: 'var(--text)' }}>{safeFormat(t.start_date, 'MMM d, yyyy')}</strong>
                    </span>
                  )}
                  {t?.due_date && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      <CalIcon /> Due: <strong style={{ color: 'var(--text)' }}>{safeFormat(t.due_date, 'MMM d, yyyy')}</strong>
                    </span>
                  )}
                  {t?.priority && <Badge value={t.priority} type="priority" />}
                  {t?.status && <Badge value={t.status} type="status" />}
                </div>
              </div>
              <Link to={`/tasks`} className="btn-secondary text-xs flex-shrink-0">View Tasks</Link>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              {[
                { label: 'Activities',  value: s.total },
                { label: 'Completed',   value: s.completed },
                { label: 'Est. Hours',  value: `${parseFloat(s.estimated_hours).toFixed(1)}h` },
                { label: 'Actual Hours',value: `${parseFloat(s.actual_hours).toFixed(1)}h` },
              ].map(stat => (
                <div key={stat.label} className="text-center p-3 rounded-xl" style={{ background: 'var(--surface-alt)' }}>
                  <p className="text-xl font-bold" style={{ color: 'var(--text)' }}>{stat.value}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between text-xs" style={{ color: 'var(--text-secondary)' }}>
                <span>Progress: {pct}%</span>
                <span>{done}/{total} done · {parseFloat(s.actual_hours).toFixed(1)}h / {parseFloat(s.estimated_hours).toFixed(1)}h</span>
              </div>
              <div className="h-2.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                <div className="h-full rounded-full transition-all"
                  style={{ width: `${pct}%`, background: pct === 100 ? '#10b981' : '#2E539F' }} />
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      {/* This short-circuited on !selectedTaskId, so the list never rendered
          without a task — whatever the data said. The counts in the tabs were
          right the whole time; this branch threw the rows away before they
          reached the table.

          There is no 'choose a task' empty state any more, because choosing
          no task is now a legitimate view: all of them. */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <EmptyState icon={Layers}
            title={scope === 'deleted' ? 'Nothing deleted' : selectedTaskId ? 'No activities yet' : 'No activities'}
            message={scope === 'deleted'
              ? 'Activities you delete appear here, and can be restored.'
              : selectedTaskId
                ? 'Add activities to break this task into trackable units of work.'
                : 'Nothing matches those filters.'}
            action={canWrite && selectedTaskId && (<button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm"><PlusIcon /> Add First Activity</button>)} />
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b" style={{ borderColor: 'var(--border)', background: 'var(--surface-alt)' }}>
                {/* The Task column only appears when you are not already
                    looking at one task — otherwise it repeats the same value
                    on every row. */}
                <th className="px-3 py-3 w-10">
                  <SelectAll allChecked={sel.allChecked} someChecked={sel.someChecked}
                    onToggle={sel.toggleAll} />
                </th>
                {['', 'Activity', ...(selectedTaskId ? [] : ['Task']), 'Status', 'Priority', 'Window', 'Est h', 'Actual h', 'Effort', 'Assigned', ''].map((h, i) => (
                  <th key={i} className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id} className="border-b hover:bg-[var(--surface-alt)] transition-colors cursor-pointer"
                  style={{ borderColor: 'var(--border)', opacity: isSettled(a.status) ? 0.75 : 1 }}
                  onClick={() => navigate(`/activities/${a.id}`)}>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <RowCheck checked={sel.isSelected(a.id)}
                        onToggle={() => sel.toggle(a.id)} label={a.title} />
                    </td>

                  <td className="pl-3 py-3 w-8" onClick={e => { e.stopPropagation(); handleStatusCycle(a); }}>
                    <button title="Click to cycle status"><StatusIcon status={a.status} /></button>
                  </td>

                  <td className="px-3 py-3">
                    <p className={`font-medium ${a.status === 'Completed' ? 'line-through' : ''}`}
                      style={{ color: 'var(--text)' }}>{a.title}</p>
                    {a.description && (
                      <p className="text-xs mt-0.5 line-clamp-1" style={{ color: 'var(--text-secondary)' }}>{a.description}</p>
                    )}
                  </td>

                  {/* Matches the conditional header above. If one appears without the
                      other, every column below it shifts by one. */}
                  {!selectedTaskId && (
                    <td className="px-3 py-3">
                      <p className="text-xs" style={{ color: 'var(--text)' }}>{a.task_title || '—'}</p>
                      {a.project_name && (
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{a.project_name}</p>
                      )}
                    </td>
                  )}

                  <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                    <select value={a.status} disabled={!canEdit(a)}
                      onChange={e => handleInlineUpdate(a, { status: e.target.value })}
                      className="text-xs rounded px-1.5 py-1 border font-medium cursor-pointer outline-none"
                      style={{
                        background: 'var(--surface)',
                        borderColor: statusStyle(a.status).border,
                        color: statusStyle(a.status).text,
                      }}>
                      {/* All five. This list was hardcoded to three, so Hold
                          and Skipped could not be set from the table at all —
                          and an activity already in one of them rendered with
                          no matching option. */}
                      {WORK_STATUSES.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </td>

                  <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                    <select value={a.priority || 'Medium'}
                      onChange={e => handleInlineUpdate(a, { priority: e.target.value })}
                      className="text-xs rounded px-1.5 py-1 border font-medium cursor-pointer outline-none"
                      style={{
                        background: 'var(--surface)', borderColor: 'var(--border)',
                        color: a.priority === 'Critical' ? '#ef4444'
                             : a.priority === 'High'     ? '#d97706'
                             : a.priority === 'Low'      ? 'var(--text-secondary)' : '#2E539F',
                      }}>
                      {PRIORITY_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </td>

                  <td className="px-3 py-3 hidden lg:table-cell text-xs" style={{ color: 'var(--text-secondary)' }}>
                    <div className="flex items-center gap-1">
                      {a.start_date ? safeFormat(a.start_date, 'MMM d') : <span style={{ color: 'var(--border)' }}>—</span>}
                      {(a.start_date || a.due_date) && <span>→</span>}
                      {a.due_date ? safeFormat(a.due_date, 'MMM d') : <span style={{ color: 'var(--border)' }}>—</span>}
                    </div>
                  </td>

                  {/* Estimated hours - still manually editable, this is a plan, not a fact */}
                  <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                    <input type="number" min="0" step="0.5"
                      value={a.estimated_hours || ''}
                      onChange={async e => handleInlineUpdate(a, { estimated_hours: e.target.value })}
                      className="w-16 text-xs rounded px-2 py-1 border text-center"
                      style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--text)' }}
                      title="Estimated hours" />
                  </td>

                  {/* Actual hours - READ ONLY now, computed from logged time. Click the clock icon to log time. */}
                  <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1.5">
                      <span className="w-16 text-xs rounded px-2 py-1 border text-center inline-block"
                        style={{
                          background: 'var(--surface-alt)',
                          borderColor: parseFloat(a.actual_hours) > parseFloat(a.estimated_hours) ? '#ef4444' : 'var(--border)',
                          color: parseFloat(a.actual_hours) > parseFloat(a.estimated_hours) ? '#ef4444' : 'var(--text-secondary)',
                        }}
                        title="Computed from logged time - not directly editable">
                        {parseFloat(a.actual_hours || 0).toFixed(2)}
                      </span>
                      <button onClick={() => setLogTimeFor(a)} title="Log time"
                        className="p-1 rounded hover:bg-[var(--surface-alt)]" style={{ color:'#2E539F' }}>
                        <ClockLogIcon/>
                      </button>
                    </div>
                  </td>

                  <td className="px-3 py-3 hidden xl:table-cell" style={{ minWidth: 120 }}>
                    <EffortBar estimated={a.estimated_hours} actual={a.actual_hours} />
                  </td>

                  <td className="px-3 py-3 hidden lg:table-cell">
                    {a.assigned_to_name
                      ? <div className="flex items-center gap-1.5">
                          <div className="w-6 h-6 rounded text-xs font-bold text-white flex items-center justify-center"
                            style={{ background: a.assigned_to_color || '#2E539F' }}>{a.assigned_to_name[0]}</div>
                          <span className="text-xs truncate max-w-[80px]" style={{ color: 'var(--text-secondary)' }}>{a.assigned_to_name}</span>
                        </div>
                      : <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>—</span>}
                  </td>

                  <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1">
                      {scope === 'deleted' ? (
                        <>
                          {/* In the bin, the only actions that make sense are to put it
                              back or to take it out of your own Deleted tab. */}
                          <button onClick={e=>{e.stopPropagation();restoreActivity(a);}}
                            className="text-xs px-2.5 py-1 rounded-lg border flex items-center gap-1 hover:bg-[var(--surface-alt)]"
                            style={{ borderColor:'var(--border)', color:'#2E539F' }}>
                            <RotateCcw size={12} /> Restore
                          </button>
                          <button onClick={e=>{e.stopPropagation();discardActivity(a);}}
                            className="text-xs px-2.5 py-1 rounded-lg border flex items-center gap-1 hover:bg-red-50 dark:hover:bg-red-900/20"
                            style={{ borderColor:'var(--border)', color:'#ef4444' }}>
                            <Trash2 size={12} /> Delete
                          </button>
                        </>
                      ) : (
                        <>
                      {canEdit(a) && (
                        <button onClick={() => openEdit(a)}
                        className="p-1.5 rounded hover:bg-[var(--surface-alt)]"
                        style={{ color: 'var(--text-secondary)' }}><PencilIcon /></button>
                      )}
                      {canRemove(a) && (
                        <button onClick={() => setDeleteId(a.id)}
                        className="p-1.5 rounded text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"><TrashIcon /></button>
                      )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create / Edit Modal ──────────────────────────────────────────────── */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}
        title={editActivity ? 'Edit Activity' : 'Add Activity'} size="md">
        <div className="space-y-4">

          {taskInfo && (
            <div className="rounded-xl p-3 border" style={{ background: 'var(--surface-alt)', borderColor: 'var(--border)' }}>
              <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-secondary)' }}>Task</p>
              <p className="font-medium text-sm" style={{ color: 'var(--text)' }}>{taskInfo.title}</p>
              <div className="flex items-center gap-4 mt-1.5">
                {taskInfo.start_date && (
                  <span className="text-xs flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
                    <CalIcon /> Start: <strong>{safeFormat(taskInfo.start_date, 'MMM d, yyyy')}</strong>
                  </span>
                )}
                {taskInfo.due_date && (
                  <span className="text-xs flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
                    <CalIcon /> Due: <strong>{safeFormat(taskInfo.due_date, 'MMM d, yyyy')}</strong>
                  </span>
                )}
              </div>
            </div>
          )}

          {!taskFilter && !editActivity && !selectedTaskId && (
            <FormField label="Task" required>
              <select className="select" value={form.task_id}
                onChange={e => setForm(f => ({ ...f, task_id: e.target.value }))}>
                <option value="">Select task</option>
                {tasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
            </FormField>
          )}

          <FormField label="Activity Title" required>
            <input className="input" value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Configure authentication module" />
          </FormField>

          <FormField label="Description">
            <textarea className="input" rows={2} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="What needs to be done?" />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Available from">
              <input type="date" className="input" value={form.start_date}
                min={taskBounds().min || undefined}
                max={(form.due_date || taskBounds().max) || undefined}
                title={rangeHint()}
                onChange={e => setForm(f => ({ ...f, start_date: clamp(e.target.value,
                  { min: taskBounds().min, max: f.due_date || taskBounds().max }) }))} />
            </FormField>
            <FormField label="Due by">
              <input type="date" className="input" value={form.due_date}
                min={(form.start_date || taskBounds().min) || undefined}
                max={taskBounds().max || undefined}
                title={rangeHint()}
                onChange={e => setForm(f => ({ ...f, due_date: clamp(e.target.value,
                  { min: f.start_date || taskBounds().min, max: taskBounds().max }) }))} />
            </FormField>
            <div className="col-span-2 -mt-2">
              <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{DATE_EXPLAINER}</p>
              <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)', opacity:.8 }}>{rangeHint()}</p>
            </div>
          </div>

          {/* A standing duty — "search for tenders", "check the platform is
              up" — is ONE activity that runs for a long time, done over and
              over. It stays open; each day is ticked on its own in the To-Do
              list. "Available from" is what it repeats from, and for a weekly
              repeat it is also what picks the weekday. */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Repeats">
              <select className="select" value={form.recurrence || 'none'}
                onChange={e => setForm(f => ({ ...f, recurrence: e.target.value }))}>
                {RECURRENCE.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </FormField>
            {form.recurrence !== 'none' && (
              <FormField label="Repeat until">
                <input type="date" className="input" value={form.recurrence_until}
                  min={form.start_date || undefined}
                  onChange={e => setForm(f => ({ ...f, recurrence_until: e.target.value }))}/>
              </FormField>
            )}
            {form.recurrence !== 'none' && (
              <div className="col-span-2 -mt-2">
                <p className="text-xs" style={{ color:'var(--text-secondary)' }}>
                  {!form.start_date
                    ? 'Set "Available from" — it is the date the repeat starts from.'
                    : form.recurrence === 'weekly'
                      ? `Repeats every ${WEEKDAY_OF(form.start_date)}, from ${form.start_date}${form.recurrence_until ? ` until ${form.recurrence_until}` : ', with no end date'}.`
                      : `Repeats ${RECURRENCE.find(o => o.v === form.recurrence)?.l.toLowerCase()} from ${form.start_date}${form.recurrence_until ? ` until ${form.recurrence_until}` : ', with no end date'}.`}
                </p>
                <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)', opacity:.8 }}>
                  It stays open and is never marked Completed by ticking a day.
                </p>
              </div>
            )}
          </div>

          <FormField label="Estimated Hours" required>
            <input type="number" min="0" step="0.5" className="input"
              value={form.estimated_hours}
              onChange={e => setForm(f => ({ ...f, estimated_hours: e.target.value }))}
              placeholder="e.g. 4.0" />
          </FormField>

          {editActivity && (
            <div className="rounded-lg px-3 py-2.5 text-xs flex items-center justify-between" style={{ background:'var(--surface-alt)' }}>
              <span style={{ color:'var(--text-secondary)' }}>
                Actual hours logged so far: <strong style={{ color:'var(--text)' }}>{parseFloat(editActivity.actual_hours||0).toFixed(2)}h</strong>
              </span>
              <button type="button" onClick={() => { setModalOpen(false); setLogTimeFor(editActivity); }}
                className="text-brand-500 hover:underline font-medium">Log time →</button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Status">
              <SelectField value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                options={STATUS_OPTS} placeholder="" />
            </FormField>
            <FormField label="Priority">
              <SelectField value={form.priority || 'Medium'}
                onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                options={PRIORITY_OPTS} placeholder="" />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Assign To" required>
              <select className="select" value={form.assigned_to}
                onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}>
                <option value="">Select a person…</option>
                {users.filter(u => u.is_active).map(u =>
                  <option key={u.id} value={u.id}>{u.name}{u.id === me?.id ? ' (me)' : ''}</option>)}
              </select>
            </FormField>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setModalOpen(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleSave}
              disabled={saving || !form.title || !form.assigned_to
                || (!form.task_id && !selectedTaskId && !editActivity)
                /* A repeat with no anchor can never fire; the API rejects it
                   too, but the button should not invite the round trip. */
                || (form.recurrence !== 'none' && !form.start_date)}
              className="btn-primary flex items-center gap-2">
              {saving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin" />}
              {editActivity ? 'Update Activity' : 'Add Activity'}
            </button>
          </div>
        </div>
      </Modal>

      <LogTimeModal isOpen={!!logTimeFor} onClose={() => setLogTimeFor(null)} activity={logTimeFor} onLogged={loadActivities} />

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete}
        title="Delete Activity" message="Delete this activity permanently? Task effort totals will be updated." />
    <Pager {...paging} onChange={paging.setOffset} noun="activities" />
    <BulkBar bulk={activitiesAPI.bulk} view={scope === 'deleted' ? 'deleted' : 'live'}
      selected={sel.selected} label="activity" labelPlural="activities"
      onClear={sel.clear} onDone={loadActivities} />
    </div>
  );
}
